@if (chat.state() === 'icon' && !chat.hidden()) {
  <button type="button"
          class="fixed bottom-6 right-6 z-[901] w-20 h-20 rounded-xl bg-white border border-surface-200 shadow-lg flex items-center justify-center hover:bg-surface-50 cursor-pointer"
          aria-label="Open TAP Assistant"
          pTooltip="TAP Assistant"
          (click)="chat.open()">
    <img src="assets/images/chat-icon.svg" alt="" class="w-16 h-16">
    @if (chat.unread()) {
      <span class="absolute top-1 right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-white"></span>
    }
    @if (chat.busy()) {
      <span class="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-surface-200 flex items-center justify-center text-[10px] text-surface-600">
        <i class="fa-regular fa-spinner fa-spin"></i>
      </span>
    }
  </button>
}

@if (chat.state() === 'full') {
  <div class="fixed inset-0 z-[900] bg-black/30"
       animate.enter="chat-backdrop-enter"
       animate.leave="chat-backdrop-leave"
       (click)="chat.exitFull()"></div>
}

<!-- One element for panel and full screen; only its box changes. -->
@if (chat.state() !== 'icon') {
  <section role="dialog"
           aria-label="TAP Assistant"
           class="fixed z-[901] flex flex-col bg-white rounded-[20px] border border-surface-200 shadow-lg overflow-hidden"
           [ngClass]="chat.state() === 'full'
             ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] h-[85vh]'
             : 'bottom-6 right-6 w-[26rem] h-[36rem] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-3rem)]'">
    <div class="flex items-center gap-3 px-4 py-3 border-b border-surface-200 shrink-0">
      <img src="assets/images/chat-icon.svg" alt="" class="w-9 h-9 shrink-0">
      <b class="text-surface-700 text-lg font-semibold font-['Inter']">TAP Assistant</b>
      <div class="ml-auto flex gap-1">
        <button type="button"
                class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface-100 text-surface-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="New chat"
                pTooltip="New Chat"
                tooltipPosition="bottom"
                [disabled]="chat.busy() || !chat.entries().length"
                (click)="chat.reset()">
          <i class="fa-regular fa-pen-to-square"></i>
        </button>
        <button type="button"
                class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface-100 text-surface-600 cursor-pointer"
                [attr.aria-label]="chat.state() === 'full' ? 'Exit full screen' : 'Full screen'"
                [pTooltip]="chat.state() === 'full' ? 'Collapse' : 'Expand'"
                tooltipPosition="bottom"
                (click)="chat.toggleFull()">
          <i class="fa-regular" [ngClass]="chat.state() === 'full' ? 'fa-compress' : 'fa-expand'"></i>
        </button>
        <button type="button"
                class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface-100 text-surface-600 cursor-pointer"
                aria-label="Hide chat"
                pTooltip="Hide"
                tooltipPosition="bottom"
                (click)="confirmHide()">
          <i class="fa-regular fa-eye-slash"></i>
        </button>
        <button type="button"
                class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface-100 text-surface-600 cursor-pointer"
                aria-label="Minimise"
                pTooltip="Minimise"
                tooltipPosition="bottom"
                (click)="chat.minimise()">
          <i class="fa-regular fa-minus"></i>
        </button>
      </div>
    </div>

    <div #chain
         class="flex-1 min-h-0 overflow-y-auto [scrollbar-gutter:stable] flex flex-col gap-4 px-4 py-4"
         (scroll)="onScroll()">
      @for (entry of chat.entries(); track $index) {
        @if (entry.role === 'user') {
          <div class="w-full flex flex-col gap-1 items-end">
            <div class="text-surface-500 text-2xs font-normal font-['Inter'] mx-2">{{ entry.at | date: 'dd/MM/yyyy  HH:mm' }}</div>
            <div class="max-w-full px-4 py-1 min-h-12 flex items-center rounded-2xl rounded-br-none bg-teal-500 text-white text-xs font-normal font-['Inter'] leading-5 whitespace-pre-wrap break-words">{{ entry.text }}</div>
          </div>
        } @else {
          <div class="w-full flex gap-3">
            <div class="shrink-0 mt-5 w-10 h-10 rounded-full flex items-center justify-center bg-teal-400 text-white text-lg font-normal font-['Inter'] select-none">TA</div>
            <div class="flex-1 min-w-0 flex flex-col gap-1 items-start">
              <div class="flex items-center gap-2 text-surface-500 text-xs font-normal font-['Inter'] mx-2">
                <span class="font-semibold text-2xs">TAP Assistant</span>
                <span class="text-2xs">{{ entry.at | date: 'dd/MM/yyyy  HH:mm' }}</span>
              </div>
              <div class="max-w-full min-w-0 px-4 py-2 min-h-12 rounded-2xl rounded-bl-none bg-teal-50 text-surface-700 text-xs font-normal font-['Inter'] leading-5">
                @for (tool of entry.tools; track $index) {
                  <div class="text-2xs text-surface-500">
                    @switch (tool.state) {
                      @case ('running') { <i class="fa-regular fa-spinner fa-spin"></i> }
                      @case ('ok') { <i class="fa-regular fa-circle-check text-green-600"></i> }
                      @case ('failed') { <i class="fa-regular fa-circle-xmark text-red-600"></i> }
                    }
                    {{ tool.name }}
                  </div>
                }
                @if (entry.text) {
                  <chat-markdown [text]="entry.text"></chat-markdown>
                } @else if (entry.pending) {
                  <div class="text-surface-500">
                    <i class="fa-regular fa-ellipsis fa-fade"></i> {{ chat.elapsed() }} s
                  </div>
                }
                @if (entry.submissionId) {
                  <div class="mt-1"><a [routerLink]="['/ta', entry.submissionId]" (click)="onOpenDraft($event)" class="text-primary-600 underline">Open draft</a></div>
                }
                @if (entry.error) {
                  <div class="mt-1 text-red-600">{{ entry.error }}</div>
                }
              </div>
            </div>
          </div>
        }
      } @empty {
        <div class="m-auto text-surface-500 text-sm font-normal font-['Inter'] italic text-center">
          Ask about travel allowances, TAs or travellers.
        </div>
      }
    </div>

    <div class="shrink-0 border-t border-surface-200 px-4 py-3 flex items-end gap-3">
      <textarea #input
                rows="2"
                [(ngModel)]="draft"
                (keydown.enter)="onEnter($event)"
                placeholder="Ask TAP Assistant…"
                aria-label="Message TAP Assistant"
                class="flex-1 min-w-0 resize-none max-h-40 [field-sizing:content] px-4 py-2 rounded-lg border border-surface-300 focus:border-primary-500 outline-none text-xs leading-[18px] font-['Inter'] text-surface-700"></textarea>
      <!-- mousedown preventDefault keeps focus in the composer across the click. -->
      <button type="button"
              aria-label="Send"
              [disabled]="chat.busy() || !draft.trim()"
              (mousedown)="$event.preventDefault()"
              (click)="submit()"
              class="shrink-0 w-9 h-9 pt-1 pr-1 rounded-full flex items-center justify-center leading-none text-base
                     text-white bg-primary-500 enabled:hover:bg-primary-600 disabled:bg-primary-300
                     cursor-pointer disabled:cursor-not-allowed">
        <i class="fa-solid fa-paper-plane"></i>
      </button>
    </div>
  </section>
}
